import { Link } from "react-router-dom";
import { Heart, Gem, Cake, Baby, Briefcase, Users, UtensilsCrossed, Coffee, Check, Truck } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { eventTypes, formules, prestationsComplementaires, tarifs, minimums, tarifsNote, faq, methode } from "../config/traiteur";

const icons = { Heart, Gem, Cake, Baby, Briefcase, Users, UtensilsCrossed, Coffee };

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faq.map((f) => ({
    "@type": "Question",
    "name": f.q,
    "acceptedAnswer": { "@type": "Answer", "text": f.a },
  })),
};

const serviceJsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": "Traiteur événementiel",
  "provider": { "@type": "Restaurant", "name": "TN FOOD" },
  "areaServed": ["France"],
  "description": "Buffets, cocktails dînatoires, repas assis et plateaux repas pour mariages, fiançailles, anniversaires, baptêmes et événements d'entreprise.",
};

export default function Traiteur() {
  return (
    <main id="contenu-principal">
      <Seo
        title="Traiteur événementiel à Troyes — Mariages, fiançailles, anniversaires | TN FOOD"
        description="Traiteur tunisien pour mariages, fiançailles, anniversaires, baptêmes et événements d'entreprise partout en France, au départ de Sainte-Savine près de Troyes. Buffets, cocktails dînatoires, service à table. Devis sous 48 h."
        path="/traiteur"
        jsonLd={[faqJsonLd, serviceJsonLd]}
      />

      <section className="bg-nuit text-cream py-16 md:py-24" data-testid="traiteur-hero">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <Reveal>
            <h1 className="font-display text-4xl md:text-5xl font-bold max-w-3xl">Traiteur événementiel : la générosité tunisienne pour vos plus beaux moments</h1>
            <p className="mt-6 text-lg text-cream/85 max-w-2xl leading-relaxed">
              Mariages, fiançailles, anniversaires, baptêmes, événements d'entreprise, fêtes familiales et religieuses : nous composons une prestation sur mesure, partout en France.
            </p>
            <Link to="/contact#devis" data-testid="traiteur-cta-devis" className="mt-8 inline-block rounded-full bg-saffron text-cream px-8 py-3.5 font-semibold transition-all duration-300 hover:bg-gold hover:-translate-y-0.5">
              Demander un devis gratuit
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Types d'événements */}
      <section className="py-16 md:py-24" data-testid="traiteur-evenements">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <Reveal className="mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-encre">Les événements que nous couvrons</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {eventTypes.map((ev, i) => {
              const Icon = icons[ev.icon];
              return (
                <Reveal key={ev.title} delay={i * 0.06}>
                  <article className="bg-white rounded-2xl p-6 border border-terracotta/10 shadow-sm hover:shadow-md transition-shadow duration-300 h-full">
                    <Icon className="h-8 w-8 text-terracotta" strokeWidth={1.5} aria-hidden="true" />
                    <h3 className="font-display text-xl font-semibold text-encre mt-4">{ev.title}</h3>
                    <p className="mt-2 text-sm text-taupe leading-relaxed">{ev.description}</p>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Formules */}
      <section className="bg-cream py-16 md:py-24" data-testid="traiteur-formules">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <Reveal className="mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-encre">Nos formules</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {formules.map((f, i) => (
              <Reveal key={f.title} delay={i * 0.06}>
                <article className="bg-white rounded-2xl p-6 border border-saffron/20 shadow-sm h-full">
                  <h3 className="font-display text-xl font-semibold text-encre">{f.title}</h3>
                  <p className="mt-2 text-sm text-taupe leading-relaxed">{f.description}</p>
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12">
            <h3 className="font-display text-2xl font-semibold text-encre mb-4">Prestations complémentaires</h3>
            <ul className="grid md:grid-cols-2 gap-3">
              {prestationsComplementaires.map((p) => (
                <li key={p} className="flex gap-3 text-taupe">
                  <Check className="h-5 w-5 shrink-0 text-olive" strokeWidth={1.5} aria-hidden="true" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Tarifs */}
      <section className="py-16 md:py-24" data-testid="traiteur-tarifs">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-5xl">
          <Reveal className="mb-10">
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-encre">Tarifs indicatifs</h2>
            <p className="mt-3 text-taupe">Fourchettes indicatives HT, selon la prestation, le nombre de convives, la saison et les options.</p>
          </Reveal>
          <Reveal>
            <div className="bg-white rounded-2xl border border-terracotta/10 shadow-sm overflow-hidden">
              <ul className="divide-y divide-terracotta/10">
                {tarifs.map((t) => (
                  <li key={t.label} className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 px-6 py-4">
                    <span className="text-encre font-medium">{t.label}</span>
                    <span className="text-terracotta font-bold whitespace-nowrap">{t.price}</span>
                  </li>
                ))}
              </ul>
            </div>
            <p className="mt-4 flex gap-2 text-sm text-taupe">
              <Truck className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={1.5} aria-hidden="true" />
              {minimums}
            </p>
            <p className="mt-3 text-sm text-taupe italic">{tarifsNote}</p>
          </Reveal>
        </div>
      </section>

      {/* Méthode */}
      <section className="bg-bordeaux text-cream py-16 md:py-24" data-testid="traiteur-methode">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <Reveal className="mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-semibold">Comment ça marche ?</h2>
          </Reveal>
          <ol className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {methode.map((m, i) => (
              <Reveal key={m.step} delay={i * 0.1}>
                <li>
                  <span className="font-display text-5xl font-bold text-gold/40">{m.step}</span>
                  <h3 className="font-display text-xl font-semibold mt-2">{m.title}</h3>
                  <p className="mt-2 text-sm text-cream/80 leading-relaxed">{m.description}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24" data-testid="traiteur-faq">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-3xl">
          <Reveal className="mb-10 text-center">
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-encre">Questions fréquentes</h2>
          </Reveal>
          <Accordion type="single" collapsible className="w-full" data-testid="traiteur-faq-accordion">
            {faq.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`}>
                <AccordionTrigger data-testid={`faq-question-${i}`} className="text-left font-semibold text-encre">{f.q}</AccordionTrigger>
                <AccordionContent className="text-taupe leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <Reveal className="mt-12 text-center">
            <Link to="/contact#devis" data-testid="traiteur-bottom-cta" className="inline-block rounded-full bg-saffron text-cream px-8 py-3.5 font-semibold transition-all duration-300 hover:bg-saffron-dark hover:-translate-y-0.5">
              Demander un devis traiteur
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
